import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getHeader, getPOAExport } from "../../services/plaOperativo/headerPlanOperativoServices";
import logo from '../../assets/logo_exportable/logo.jpg';

const ExportPoa = forwardRef(({ selectedProject, valoresJsonPeso }, ref) => {
    const [headerPO, setHeaderPO] = useState(null);
    const [fasesResult, setFaseResult] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!selectedProject) return;
        const fetchProjects = async () => {
            try {
                const { data } = await getHeader(selectedProject);
                setHeaderPO(data?.encabezado[0] || null);
            } catch (error) {
                console.error("Error fetching projects:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchProjects();
    }, [selectedProject]);

    useEffect(() => {
        if (!selectedProject) return;
        const fetchPOA = async () => {
            setLoading(true);
            try {
                const { data } = await getPOAExport(selectedProject);
                setFaseResult(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error fetching POA:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchPOA();
    }, [selectedProject]);


    function logTotPesoPorMesAnioPorFase(dataJSONPeso) {
        // Objeto que guardará los acumuladores por fase, subdivididos en P y E.
        // Ej: acumuladoresPorFase[faseId] = { P: {}, E: {} }
        const acumuladoresPorFase = {};

        // Recorremos las "fases" (elementos del array dataJSONPeso)
        dataJSONPeso.forEach((fase) => {
            const faseId = fase.fase_id || "fase-sin-id";

            // Si no existe, inicializamos la estructura con P y E vacíos
            if (!acumuladoresPorFase[faseId]) {
                acumuladoresPorFase[faseId] = {
                    P: {},
                    E: {},
                };
            }

            if (!fase.children) return; // Sin actividades, skip

            fase.children.forEach((tarea) => {
                // Ajusta si tu "pesoPorcentual" es realmente un factor o un %.
                // Aquí supongo que lo usas "tal cual":
                const pesoEntero = parseFloat(tarea.pesoPorcentual) || 0;
                const peso = pesoEntero / 1; // o /100 si en BD 3 significa 3%

                if (!tarea.children) return; // Sin indicadores, skip

                tarea.children.forEach((indicador) => {
                    const meta = parseFloat(indicador.meta) || 0;
                    if (meta <= 0 || peso <= 0) return; // Evitar /0

                    // --- PROYECTADO (P) ---
                    if (indicador.cronogramasP) {
                        indicador.cronogramasP.forEach((crono) => {
                            const p = parseFloat(crono.avance) || 0;
                            const mes = crono.mes;
                            const ano = crono.ano;
                            const keyMesAnio = `${mes}-${ano}`;

                            const parcial = (p / meta) * peso;

                            if (!acumuladoresPorFase[faseId].P[keyMesAnio]) {
                                acumuladoresPorFase[faseId].P[keyMesAnio] = 0;
                            }
                            acumuladoresPorFase[faseId].P[keyMesAnio] += parcial;
                        });
                    }

                    // --- EJECUTADO (E) ---
                    if (indicador.cronogramasE) {
                        indicador.cronogramasE.forEach((crono) => {
                            const e = parseFloat(crono.avance) || 0;
                            const mes = crono.mes;
                            const ano = crono.ano;
                            const keyMesAnio = `${mes}-${ano}`;

                            const parcial = (e / meta) * peso;

                            if (!acumuladoresPorFase[faseId].E[keyMesAnio]) {
                                acumuladoresPorFase[faseId].E[keyMesAnio] = 0;
                            }
                            acumuladoresPorFase[faseId].E[keyMesAnio] += parcial;
                        });
                    }
                });
            });
        });
        // *** REDONDEO AL FINAL ***
        // Recorremos cada fase, y dentro de cada fase, cada tipo (P, E) y cada "mes-año"
        for (const faseId in acumuladoresPorFase) {
            for (const tipo of ["P", "E"]) {
                for (const keyMesAnio in acumuladoresPorFase[faseId][tipo]) {
                    const valorAcumulado = acumuladoresPorFase[faseId][tipo][keyMesAnio];
                    // Redondear a 2 decimales
                    acumuladoresPorFase[faseId][tipo][keyMesAnio] = Number(
                        valorAcumulado.toFixed(2)
                    );
                }
            }
        }

        // Ahora construimos un objeto JSON con el formato deseado
        // (ej. "fase" y luego "p" y "e" separadas)
        const resultado = { fases: [] };

        for (const [faseId, objPE] of Object.entries(acumuladoresPorFase)) {
            // objPE = { P: {...}, E: {...} }
            resultado.fases.push({
                fase: faseId,
                p: objPE.P,
                e: objPE.E
            });
        }
        // Devolver el objeto JSON para usarlo en otra parte
        return resultado;
    }


    // Función para obtener los encabezados de cronograma
    const getCronogramaHeaders = (fase, modo) => {

        let totalPesoPorcentual = 0;
        const actividadesArray = Array.isArray(fase.actividades) ? fase.actividades : [fase.actividades];
        const JsonPeso = logTotPesoPorMesAnioPorFase(actividadesArray);
        const headerRow1 = ["ACTIVIDAD", "TAREA", "INDICADORES", "META", "FECHA INICIO", "FECHA FIN", "P/E"];
        const headerRow2 = Array(7).fill(""); // Segunda fila con espacios vacíos para alineación
        const yearMonthMap = new Map();
        const mesesPorAnio = {};
        // Recopilar años y meses en un Map
        fase.actividades?.forEach(act => {
            act.children?.forEach(tarea => {
                totalPesoPorcentual += tarea.pesoPorcentual;
                tarea.children?.forEach(indicador => {
                    indicador.cronogramaconfig?.forEach(({ anio, fechas }) => {

                        if (!yearMonthMap.has(anio)) {
                            yearMonthMap.set(anio, new Set());
                        }
                        fechas.forEach(mes => yearMonthMap.get(anio).add(mes));
                        mesesPorAnio[anio] = fechas;

                    });
                });
            });
        });


        const sortedYears = Array.from(yearMonthMap.keys()).sort();
        const monthOrder = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
        sortedYears.forEach(anio => {
            const mesesOrdenados = Array.from(yearMonthMap.get(anio)).sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));
            // Agregar el prefijo "Año: " al año
            headerRow1.push(`CRONOGRAMA : ${anio}`, ...Array(mesesOrdenados.length * 2 - 1).fill(""));
            // Insertamos los meses y sus respectivos %Avance
            mesesOrdenados.forEach(mes => {
                const fases = JsonPeso.fases || [];
                if (!fases.some(fase => fase.fase === '3')) {
                    const PData = (fases.find(fase => fase.p?.[`${mes}-${anio}`] !== undefined)?.p[`${mes}-${anio}`] ?? 0) + "%";
                    const EData = (fases.find(fase => fase.e?.[`${mes}-${anio}`] !== undefined)?.e[`${mes}-${anio}`] ?? 0) + "%";

                    headerRow2.push(`${mes}\n${modo === 2 ? 0 : PData}`, `%Avance\n${modo === 2 ? 0 : EData}`);

                } else {
                    headerRow2.push(`${mes}`, `%Avance`);
                }
                //const dato = JsonPeso(fase.actividades);

            });
        });
        // Determinar el texto de la fase de manera más compacta
        const faseText = (fase.cod_Fase === 1) ? "FASE DE ALISTAMIENTO" : "FASE DE EJECUCIÓN";
        // Mostrar el JSON en la consola para depuración
        // Definir encabezados adicionales
        headerRow1.push("SEGUIMIENTO ACUMULADO", "");
        headerRow2.push("META Y AVANCE ACUMULADO", "% DE AVANCE ACUMULADO");

        // Agregar columnas según la fase
        if (fase.cod_Fase !== 3) {
            headerRow1.push("PESO PORCENTUAL");
            headerRow2.push(`PESO PORCENTUAL ${faseText}\n${totalPesoPorcentual}%`);
        }
        // Agregar columnas fijas al final
        headerRow1.push("FECHA FIN REAL", "MEDIO DE VERIFICACIÓN", "RUTA SOPORTES");
        headerRow2.push("", "", "");

        return { headerRow1, headerRow2, mesesPorAnio };
    };

    const loadImageAsArrayBuffer = async (url) => {
        const response = await fetch(url);
        const blob = await response.blob();
        return await blob.arrayBuffer();
    };

    const insertLogo = async (workbook, worksheet) => {
        const arrayBuffer = await loadImageAsArrayBuffer(logo);
        const imageId = workbook.addImage({
            buffer: arrayBuffer,
            extension: 'jpeg',
        });
        // Insertar imagen
        worksheet.addImage(imageId, {
            tl: { col: 0, row: 0 }, // A1
            br: { col: 2, row: 3 }, // C4
            editAs: 'oneCell',
        });
        // Agregar bordes a las celdas alrededor de la imagen
        const borderStyle = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        };
        for (let row = 1; row <= 4; row++) {
            for (let col = 1; col <= 3; col++) {
                const cell = worksheet.getCell(row, col);
                cell.border = borderStyle;
            }
        }
    };

    function contarMeses(f1, f2) {
        const d1 = new Date(f1);
        const d2 = new Date(f2);

        let meses = (d2.getFullYear() - d1.getFullYear()) * 12 +
            (d2.getMonth() - d1.getMonth());

        // ❗ No contar mes final si el día final < día inicial
        if (d2.getDate() < d1.getDate()) {
            meses -= 1;
        }

        return meses + 1; // incluir mes inicial
    }

    const exportToExcel = async (modo) => {
        if (!headerPO) {
            console.error("No hay datos de encabezado para exportar.");
            alert("No hay datos disponibles para exportar.");
            return;
        }
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Plan Operativo Anual");
        // ====================== ESTILOS ======================
        const styles = {
            title: { font: { size: 14, bold: true }, alignment: { horizontal: "center" } },
            common: { fgColor: "FFFFFF", bgColor: "000000", fontSize: 14 },
            alignmentCenter: { horizontal: "center", vertical: "middle", wrapText: true },
            borders: {
                all: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } },
                bottomOnly: { bottom: { style: "thin" } },
                none: {},
            }
        };
        // ====================== UTILIDADES ======================
        const mergeAndSetCell = (range, value, style = styles.title, border = true, wrapText = true) => {
            const [start, end] = range.split(":");
            if (start !== end) worksheet.mergeCells(range);
            const cell = worksheet.getCell(start);
            cell.value = value;
            cell.font = {
                name: "Arial Narrow",
                size: style.font?.size || 10,
                bold: style.font?.bold || false,
                color: { argb: "000000" }
            };
            cell.alignment = {
                horizontal: style.alignment?.horizontal ?? "center",
                vertical: style.alignment?.vertical ?? "middle",
                wrapText: wrapText
            };

            if (style.fill) {
                cell.fill = style.fill;
            }
            if (border) cell.border = styles.borders.all;
            const columns = [start.replace(/\d+/g, ""), end.replace(/\d+/g, "")];
            const totalColumns = columns.length > 1 ? columns[1].charCodeAt(0) - columns[0].charCodeAt(0) + 1 : 1;
            const columnWidth = Math.max((value?.toString().length ?? 10) / totalColumns + 5, 15);
            columns.forEach(col => worksheet.getColumn(col).width = columnWidth);
            return cell;
        };

        const applyStyle = (cell, fgColor, bgColor = "000000", bold = false, fontSize = 12, align = "center") => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fgColor } };
            cell.font = { name: "Arial Narrow", size: fontSize, bold, color: { argb: "000000" } };
            cell.alignment = { horizontal: align, vertical: "middle", wrapText: true };
            cell.border = styles.borders.all;
        };

        const drawSeparator = (range = "A1:Z1") => {
            const cell = mergeAndSetCell(range, "", {
                font: { size: 10, bold: false },
                alignment: { horizontal: "center", vertical: "middle" }
            }, false, false);
            cell.border = styles.borders.none;
        };

        const setDataRow = (
            labelRange, valueRange, label, value, defaultValue, bold = false
        ) => {
            // Label: sin borde, alineado al centro
            const labelCell = mergeAndSetCell(labelRange, label, {
                font: { size: 10, bold: true },
                alignment: { horizontal: "center", vertical: "middle", wrapText: true },
                fill: {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "D9D9D9" } // Gris claro
                }
            }, false); // sin borde
            labelCell.border = {}; // aseguramos que no tenga bordes
            // Valor: borde inferior, alineado al centro
            const valueCell = mergeAndSetCell(valueRange, value ?? defaultValue, {
                font: { size: 10, bold },
                alignment: { horizontal: "center", vertical: "middle", wrapText: true }
            }, false); // sin borde general
            valueCell.border = styles.borders.bottomOnly;
            return valueCell;
        };


        // ====================== LOGO ======================
        await insertLogo(workbook, worksheet);
        // ====================== ENCABEZADOS PRINCIPALES ======================
        function generateHeader(headerPO, hasActualTermination) {
            const inicio = new Date(headerPO?.fecha_inicio);
            const corte = new Date(headerPO?.fecha_corte);
            const meses = contarMeses(inicio, corte);
            // ====================== TÍTULOS ======================
            mergeAndSetCell("C1:AE1", headerPO?.titulo ?? "", styles.title);
            const titleCell = worksheet.getCell("C1");
            titleCell.font = { size: 12, bold: true, color: { argb: "000000" } };
            titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } };
            titleCell.alignment = styles.alignmentCenter;
            worksheet.getRow(1).height = 30;
            const c2 = mergeAndSetCell("C2:AE2", headerPO?.titulo_2 ?? "");
            applyStyle(c2, styles.common.fgColor, styles.common.bgColor, false, 12);
            worksheet.getRow(2).height = 20;
            const c3 = mergeAndSetCell("C3:AE3", headerPO?.subtitulo ?? "");
            applyStyle(c3, styles.common.fgColor, styles.common.bgColor, false, 12);
            // ====================== DATOS LATERALES ======================
            const lateralLabels = [
                { cell: "AF1", text: "Código:" + headerPO?.codigo ?? "" },
                { cell: "AF2", text: "Versión:" + headerPO?.version ?? "" },
                { cell: "AF3", text: "Fecha de publicación:" + headerPO?.fecha_publicacion ?? "" }
            ];
            lateralLabels.forEach(({ cell, text }, i) => {
                mergeAndSetCell(`${cell}:AH${i + 1}`, text, styles.title);
                applyStyle(worksheet.getCell(cell), styles.common.fgColor, styles.common.bgColor, false);
            });
            // ====================== FILA 5 ======================
            drawSeparator("A4:X4");
            setDataRow("A5:A5", "B5:D5", "TERRITORIO DE INTERVENCIÓN/ZONA PDET:", headerPO?.territorio_zona ?? "", false);
            setDataRow("F5:H5", "I5:O5", "EJECUTOR / CONSULTOR:", headerPO?.children?.[0]?.ejecutor ?? "", false);
            const terminationLabel = hasActualTermination
                ? "FECHA INICIAL DE TERMINACIÓN CONTRATO /CONVENIO:"
                : "FECHA DE TERMINACIÓN CONTRATO /CONVENIO:";
            setDataRow("Q5:T5", "U5:X5", terminationLabel, headerPO?.children?.[0]?.fecha_terminacion ?? "", false);
            // ====================== FILA 7 ======================
            drawSeparator("A6:X6");
            setDataRow("A7:A7", "B7:D7", "DEPARTAMENTO:", headerPO?.departamento ?? "");
            setDataRow("F7:H7", "I7:O7", "ORGANIZACIONES BENEFICIARIAS:", headerPO?.organizacion ?? headerPO?.nota_organizacion);
            if (hasActualTermination) {
                setDataRow("Q7:T7", "U7:X7", "FECHA ACTUAL DE TERMINACIÓN CONTRATO /CONVENIO:", headerPO?.fecha_actual_terminacion ?? "");
            } else {
                setDataRow("Q7:T7", "U7:X7", "FECHA APROBACIÓN POA INICIAL:", headerPO?.children?.[0]?.fecha_aprobacion ?? "");
            }
            // ====================== FILA 8 ======================
            drawSeparator("A8:X8");
            setDataRow("A9:A9", "B9:D9", "MUNICIPIO(S):", headerPO?.municipio, "");
            setDataRow("F9:H9", "I9:O9", "VIGENCIA DEL CONTRATO/CONVENIO:", headerPO?.duracion ?? "");
            if (hasActualTermination) {
                setDataRow("Q9:T9", "U9:X9", "FECHA APROBACIÓN POA INICIAL:", headerPO?.children?.[0]?.fecha_aprobacion ?? "");
            } else {
                setDataRow("Q9:T9", "U9:X9", "FECHA DE ACTUALIZACIÓN POA:", headerPO?.fecha_actualizacion ?? "");
            }
            // ====================== FILA 9 ======================
            drawSeparator("A10:X10");
            setDataRow("A11:A11", "B11:D11", "CONVENIO/CONTRATO:", headerPO?.children?.[0]?.contrato_convenio, "");
            setDataRow("F11:H11", "I11:O11", "FECHA DE INICIO CONTRATO /CONVENIO:", headerPO?.children?.[0]?.fecha_inicio ?? "");
            if (hasActualTermination) {
                setDataRow("Q11:T11", "U11:X11", "FECHA APROBACIÓN POA ACTUAL:", headerPO?.aprobacion_poa_actual ?? "");
            } else {
                setDataRow("Q11:R11", "S11:T11", "INFORME No.", modo === 2 ? "" : meses ?? "");
                setDataRow("U11:V11", "W11:X11", "FECHA DE CORTE:", modo === 2 ? "" : headerPO?.fecha_corte ?? "");
            }
            // ====================== FILA 10 ======================
            drawSeparator("A12:X12");
            setDataRow("A13:A13", "B13:X13", "PROYECTO:", (headerPO?.factibilidad ?? "").replace(/^PROYECTO:\s+/, ""));
            drawSeparator("A14:AG14");
            // ====================== FILA FIRMAS ======================
            setDataRow("Z5:AC5", "AD5:AG5", "FIRMA SUPERVISOR TÉCNICO DEL CONVENIO:", "");
            setDataRow("Z6:AC6", "AD6:AG6", "Nombre:", headerPO?.nombre_supervisor ?? "");
            drawSeparator("Z7:AG7");
            setDataRow("Z8:AC8", "AD8:AG8", "FIRMA COORDINADORA DEL CONVENIO:", "");
            setDataRow("Z9:AC9", "AD9:AG9", "Nombre:", "Beatriz Arismendi Rios");
            if (hasActualTermination) {
                drawSeparator("Z10:AG10");
                setDataRow("Z11:AC11", "AD11:AG11", "FECHA DE ACTUALIZACIÓN POA:", headerPO?.fecha_actualizacion ?? "");
                setDataRow("Z13:AC13", "AD13:AE13", "INFORME No.", modo === 2 ? "" : meses ?? "");
                setDataRow("AF13:AF13", "AG13:AG13", "FECHA DE CORTE:", modo === 2 ? "" : headerPO?.fecha_corte ?? "");
                drawSeparator("Z12:AG12");
            } else {
                drawSeparator("Z10:AG13");
            }
            drawSeparator("AH4:AH14");
            if (!hasActualTermination) {
                drawSeparator("Y4:Y13");
            }
        }
        // Uso:
        generateHeader(headerPO, headerPO?.fecha_actual_terminacion !== "0000-00-00");
        // ====================== CONFIGURACIÓN DE COLUMNAS ======================
        const columnSettings = {
            A: 40, B: 40, C: 25, D: 25, E: 25, F: 25
        };
        Object.entries(columnSettings).forEach(([col, width]) => {
            worksheet.getColumn(col).width = width;
            worksheet.getColumn(col).alignment = {
                horizontal: col === "B" ? "left" : "center",
                vertical: "top",
                wrapText: true
            };
        });
        let rowIndex = 15; // Índice de fila para comenzar a insertar datos
        // **Recorrer las fases**
        fasesResult.forEach((fase, index) => {
            const { headerRow1, headerRow2, mesesPorAnio } = getCronogramaHeaders(fase, modo);
            const totalColumns = headerRow1.length;
            worksheet.getColumn(totalColumns - 2).width = 15; // FECHA FIN REAL
            worksheet.getColumn(totalColumns - 1).width = 30; // MEDIO DE VERIFICACIÓN
            worksheet.getColumn(totalColumns).width = 40; // RUTA SOPORTES
            const lastColumnLetter = worksheet.getColumn(totalColumns).letter;
            // Agregar una fila para la Fase
            const faseRow = worksheet.insertRow(rowIndex, [`${fase.nombre_corto}`]);
            faseRow.getCell(1).font = { bold: true, size: 14 }; // Resaltar fase
            faseRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
            faseRow.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "A6A6A6" } };
            // Unir todas las columnas en la fila de la fase
            worksheet.mergeCells(`A${rowIndex}:${lastColumnLetter}${rowIndex}`);
            const mergedCell = worksheet.getCell(`A${rowIndex}`);
            mergedCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "A6A6A6" } };
            rowIndex++;
            // **Agregar encabezados dinámicos (headerRow1 y headerRow2)**
            const headerRowMain = worksheet.addRow(headerRow1);
            const headerRowSub = worksheet.addRow(headerRow2);
            // **Aplicar estilos a headerRow1**
            headerRowMain.font = { bold: true };
            // Aplicar fondo azul
            headerRowMain.eachCell((cell) => {
                cell.alignment = { wrapText: true, horizontal: "center", vertical: "middle" };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "4472C4" } // Azul oscuro
                };
                cell.font = { color: { argb: "FFFFFF" }, bold: true }; // Texto blanco
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" }
                };
            });

            headerRowSub.font = { bold: true };
            headerRowSub.eachCell((cell) => {
                cell.alignment = { wrapText: true, horizontal: "center", vertical: "middle" };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "BFBFBF" } }; // Color gris claro
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" }
                };
            });

            // **Fusionar celdas de los años**
            let startCol = null;  // Columna donde comienza cada sección
            let currentHeader = null;  // Nombre del encabezado actual

            headerRow1.forEach((value, colIndex) => {
                // Detectar el inicio de un nuevo encabezado
                if (value.startsWith("CRONOGRAMA")) {
                    // Si ya había un bloque previo, fusionar antes de actualizar
                    if (currentHeader !== null && startCol !== null) {
                        worksheet.mergeCells(headerRowMain.number, startCol + 1, headerRowMain.number, (colIndex + 1) - 1);
                    }
                    // Actualizar valores para el nuevo bloque
                    currentHeader = value;
                    startCol = colIndex;
                }
            });
            const maxIndex = headerRow1.length - 5; // límite superior dinámico

            headerRow1.forEach((_, colIndex) => {
                const columnIndex = colIndex + 7; // Ajuste del índice de la columna
                const isRangoReducido = columnIndex >= 7 && columnIndex <= maxIndex;
                worksheet.getColumn(columnIndex).width = isRangoReducido ? 10 : 30;
            });


            // Asegurar la última fusión para el último bloque
            if (currentHeader !== null && startCol !== null) {
                worksheet.mergeCells(headerRowMain.number, startCol + 1, headerRowMain.number, (headerRow1.length - 5) - 1);
                worksheet.mergeCells(headerRowMain.number, headerRow1.length - 5, headerRowMain.number, (headerRow1.length - 3) - 1);

            }
            // Fusionar la última sección del año
            worksheet.getRow(rowIndex).height = 40; // Ajustar altura de la fila principal de encabezado
            worksheet.getRow(rowIndex).height = 35; // Ajustar altura de la segunda fila del encabezado
            rowIndex += 2; // Avanzar 2 filas porque agregamos dos encabezados
            let maxAlturaActividad = 20; // Altura base mínima

            // **Recorrer actividades dentro de la fase**
            fase.actividades.forEach((actividad) => {
                const actividadStartRow = rowIndex;
                let actividadWritten = false;
                actividad.children?.forEach((tarea) => {
                    const tareaStartRow = rowIndex;
                    let tareaWritten = false;
                    let indicadorWritten = false;

                    // **Recorrer indicadores dentro de la tarea**
                    tarea.children?.forEach((indicador) => {
                        const indicadorStartRow = rowIndex;
                        const valoresPorcentaje = {};
                        let totalE = 0;
                        let totalP = 0;
                        let progresoEPTotal = 0;

                        // Calcular el porcentaje de avance para cada mes
                        for (const anio in mesesPorAnio) {
                            if (mesesPorAnio.hasOwnProperty(anio)) {
                                const meses = mesesPorAnio[anio]; // Array de meses para el año actual
                                meses.forEach(mes => {
                                    const registroP = indicador.cronogramasP.find(item => item.ano === anio && item.mes === mes);
                                    const registroE = indicador.cronogramasE.find(item => item.ano === anio && item.mes === mes);
                                    const avanceP = Number(registroP?.avance) || 0;
                                    const avanceE = Number(registroE?.avance) || 0;

                                    const progresoPE = avanceP > 0 ? (avanceE / avanceP) * 100 : 0;

                                    valoresPorcentaje[`${anio}-${mes}`] = `${modo === 2
                                        ? 0
                                        : Math.round(progresoPE)
                                        }%`;
                                });
                            }
                        }
                        // Recorrer los indicadores para obtener los valores de avance
                        ["P", "E"].forEach((tipoPE) => {
                            const valoresPorAnio = {};
                            indicador[`cronogramas${tipoPE}`]?.forEach((item) => {
                                if (!valoresPorAnio[item.ano]) {
                                    valoresPorAnio[item.ano] = {};
                                }

                                let avance = Number(item.avance) || 0;
                                valoresPorAnio[item.ano][item.mes] = Number.isInteger(avance)
                                    ? String(Math.trunc(avance))        // entero como string sin decimales
                                    : avance.toFixed(2);                // decimal como string con 2 decimales
                            });


                            const rowValues = [];
                            const porcentajeCells = [];
                            Object.keys(mesesPorAnio).sort().forEach((anio) => {
                                mesesPorAnio[anio].forEach((mes) => {
                                    //Valor
                                    const valor = valoresPorAnio[anio]?.[mes] || 0;
                                    const porcentaje = valoresPorcentaje[`${anio}-${mes}`] || 0;
                                    if (tipoPE === "P") {
                                        totalP += parseFloat(valor);
                                        rowValues.push(valor);
                                        rowValues.push("");
                                    } else {
                                        totalE += parseFloat(valor);
                                        rowValues.push(modo === 2 ? 0 : valor);
                                        rowValues.push(modo === 2 ? 0 : porcentaje);
                                        porcentajeCells.push(rowValues.length);
                                    }
                                });
                            });
                            if (tipoPE === "E") {
                                rowValues.push(modo === 2 ? 0 : totalE);
                                progresoEPTotal = modo === 2 ? "0%" : totalP > 0 ? `${Math.floor((totalE / totalP) * 100)}%` : "0%";
                                rowValues.push(progresoEPTotal);
                                porcentajeCells.push(rowValues.length);
                            } else {
                                rowValues.push(totalP);
                                rowValues.push("");
                            }
                            const medioVerificacion = indicador.medios_verificacion[0].medio_verificacion;
                            const row = worksheet.addRow([
                                !actividadWritten ? `${actividad.codigo_actividad} - ${actividad.descripcion}` : "",
                                !tareaWritten ? `${tarea.codigo_tarea} - ${tarea.descripcion}` : "",
                                !indicadorWritten ? `${indicador.indicador}` : "",
                                indicador.meta,
                                indicador.fecha_inicio,
                                indicador.fecha_fin,
                                tipoPE,
                                ...rowValues
                            ]);

                            // Fusionar celdas de la actividad
                            if (tipoPE === "E") {
                                porcentajeCells.forEach((colIndexProgres) => {
                                    const colLetter = worksheet.getColumn(colIndexProgres + 7).letter;
                                    const mergeRange = `${colLetter}${rowIndex - 1}:${colLetter}${(rowIndex + 1) - 1}`;
                                    worksheet.mergeCells(mergeRange);
                                    const valor = rowValues[colIndexProgres - 1]; // Obtenemos el valor de la celda correspondiente
                                    worksheet.getCell(`${colLetter}${rowIndex - 1}`).value = valor;  // Asignar el valor a la celda fusionada
                                    worksheet.getCell(`${colLetter}${rowIndex - 1}`).alignment = {
                                        vertical: "middle",
                                        horizontal: "center"
                                    };
                                });
                            }
                            const lastColIndexProgres = porcentajeCells.at(-1); // Obtener el último elemento
                            if (tipoPE === "E" && lastColIndexProgres !== undefined) {
                                const fechaFinal = (modo === 2 ? "" : indicador.fecha_f_real);

                                let valores = fase.cod_Fase !== 3
                                    ? [tarea.pesoPorcentual + "%", fechaFinal, medioVerificacion, indicador.ruta]
                                    : [fechaFinal, medioVerificacion, indicador.ruta];

                                asignarValoresEnExcel(worksheet, valores, lastColIndexProgres, rowIndex);
                            }


                            function asignarValoresEnExcel(worksheet, valores, lastColIndexProgres, rowIndex) {
                                valores.forEach((valor, i) => {
                                    const colLetter = worksheet.getColumn(lastColIndexProgres + 8 + i).letter;
                                    const mergeRange = `${colLetter}${rowIndex - 1}:${colLetter}${rowIndex}`;
                                    worksheet.mergeCells(mergeRange);
                                    worksheet.getCell(`${colLetter}${rowIndex - 1}`).value = valor;
                                });
                            }
                            row.eachCell((cell, colNumber) => {
                                const alignment = colNumber <= 3 ? "left" : "center";
                                cell.alignment = { wrapText: true, vertical: "middle", horizontal: alignment };
                                if (colNumber === 2) {
                                    cell.fill = {
                                        type: "pattern",
                                        pattern: "solid",
                                        fgColor: { argb: "DAEEF3" } // Azul claro
                                    };
                                }
                                cell.border = {
                                    top: { style: "thin" },
                                    left: { style: "thin" },
                                    bottom: { style: "thin" },
                                    right: { style: "thin" }
                                };
                            });

                            actividadWritten = true;
                            tareaWritten = true;
                            indicadorWritten = true;
                            rowIndex++;
                        });

                        // Fusionar celdas de la actividad
                        worksheet.mergeCells(`B${indicadorStartRow}:B${rowIndex - 1}`); // Tarea
                        worksheet.mergeCells(`C${indicadorStartRow}:C${rowIndex - 1}`); // Indicador
                        worksheet.mergeCells(`D${indicadorStartRow}:D${rowIndex - 1}`); // Meta
                        worksheet.mergeCells(`E${indicadorStartRow}:E${rowIndex - 1}`); // Fecha Inicio
                        worksheet.mergeCells(`F${indicadorStartRow}:F${rowIndex - 1}`); // Fecha Fin
                        ["B", "C"].forEach(col => {
                            worksheet.getCell(`${col}${indicadorStartRow}`).alignment = {
                                vertical: "middle", horizontal: "left", wrapText: true
                            };
                        });
                    });

                    if (tarea.children.length > 1) {
                        worksheet.getCell(`B${tareaStartRow}`).alignment = { vertical: "middle", horizontal: "center", wrapText: true };
                    }
                });

                if (actividad.children.length > 1) {
                    worksheet.mergeCells(`A${actividadStartRow}:A${rowIndex - 1}`);
                    const cellActividad = worksheet.getCell(`A${actividadStartRow}`);
                    cellActividad.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
                    cellActividad.font = { bold: true };
                    for (let i = actividadStartRow; i < rowIndex; i++) {
                        worksheet.getRow(i).height = maxAlturaActividad;
                    }
                } else {
                    worksheet.getCell(`A${actividadStartRow}`).font = { bold: true };
                }
            });
        });

        //Porcentajes de avance
        let headers = [];
        if (valoresJsonPeso && valoresJsonPeso.fases && valoresJsonPeso.fases.length > 0) {
            headers = Object.keys(valoresJsonPeso.fases[0].p);
        }

        const data = {
            "Fase de Alistamiento": { p: {}, e: {} },
            "Fase de ejecución": { p: {}, e: {} },
            "Total Avance Proyecto": { p: {}, e: {} }
        };

        valoresJsonPeso.fases.forEach(fase => {
            const faseName = fase.fase === "1" ? "Fase de Alistamiento" : "Fase de ejecución";
            headers.forEach(header => {
                data[faseName].p[header] = fase.p[header] || 0;
                data[faseName].e[header] = fase.e[header] || 0;
            });
        });

        headers.forEach(header => {
            data["Total Avance Proyecto"].p[header] = (data["Fase de Alistamiento"].p[header] || 0) + (data["Fase de ejecución"].p[header] || 0);
            data["Total Avance Proyecto"].e[header] = (data["Fase de Alistamiento"].e[header] || 0) + (data["Fase de ejecución"].e[header] || 0);
        });

        let row = rowIndex + 2; // Espacio entre la tabla de cronograma y la tabla de avance
        if (modo === 1) {
            Object.keys(data).forEach(phase => {
                // Desplazar el nombre de la fase a la columna F (6)
                worksheet.getCell(row, 6).value = phase;
                worksheet.getCell(row, 6).font = { bold: true, size: 12 };
                worksheet.getCell(row, 6).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "A6A6A6" } };
                worksheet.getCell(row, 6).alignment = { horizontal: "center", vertical: "middle" };
                row++;

                // **Encabezados de mes/año combinados**
                headers.forEach((header, index) => {
                    worksheet.mergeCells(row, index * 2 + 7, row, index * 2 + 8);
                    worksheet.getCell(row, index * 2 + 7).value = header;
                    worksheet.getCell(row, index * 2 + 7).alignment = { horizontal: "center", vertical: "middle" };
                    worksheet.getCell(row, index * 2 + 7).font = { bold: true, color: { argb: "FFFFFF" } };
                    worksheet.getCell(row, index * 2 + 7).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "4472C4" } };
                    worksheet.getCell(row, index * 2 + 7).border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" }
                    };
                });
                row++;

                // **Encabezados P/E**
                headers.forEach((_, index) => {
                    worksheet.getCell(row, index * 2 + 7).value = "P";
                    worksheet.getCell(row, index * 2 + 8).value = "E";
                    worksheet.getCell(row, index * 2 + 7).alignment = { horizontal: "center" };
                    worksheet.getCell(row, index * 2 + 8).alignment = { horizontal: "center" };
                    worksheet.getCell(row, index * 2 + 7).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "808080" } }; // Gris oscuro
                    worksheet.getCell(row, index * 2 + 8).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "808080" } }; // Gris oscuro
                    worksheet.getCell(row, index * 2 + 7).border = worksheet.getCell(row, index * 2 + 8).border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" }
                    };
                });
                row++;

                // **Datos**
                headers.forEach((header, index) => {
                    const faseName = phase === "Fase de Alistamiento" || phase === "Fase de ejecución" ? phase : "Total Avance Proyecto";
                    worksheet.getCell(row, index * 2 + 7).value = (data[faseName].p[header] || 0).toFixed(0) + "%";
                    worksheet.getCell(row, index * 2 + 8).value = (data[faseName].e[header] || 0).toFixed(0) + "%";
                    worksheet.getCell(row, index * 2 + 7).alignment = { horizontal: "center" };
                    worksheet.getCell(row, index * 2 + 8).alignment = { horizontal: "center" };
                    worksheet.getCell(row, index * 2 + 7).border = worksheet.getCell(row, index * 2 + 8).border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" }
                    };
                });
                row++;
                // **Total Avance**
                worksheet.getCell(row, 6).value = "TOTAL AVANCE";
                worksheet.getCell(row, 6).font = { bold: true, size: 12 };
                worksheet.getCell(row, 6).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "A6A6A6" } };
                worksheet.getCell(row, 6).alignment = { horizontal: "center", vertical: "middle" };
                const acumuladosP = headers.map(header => {
                    const faseName = phase === "Fase de Alistamiento" || phase === "Fase de ejecución" ? phase : "Total Avance Proyecto";
                    return data[faseName].p[header];
                }).reduce((acc, val) => {
                    acc.push((acc.length > 0 ? acc[acc.length - 1] : 0) + (val || 0));
                    return acc;
                }, []);

                const acumuladosE = headers.map(header => {
                    const faseName = phase === "Fase de Alistamiento" || phase === "Fase de ejecución" ? phase : "Total Avance Proyecto";
                    return data[faseName].e[header];
                }).reduce((acc, val) => {
                    acc.push((acc.length > 0 ? acc[acc.length - 1] : 0) + (val || 0));
                    return acc;
                }, []);

                headers.forEach((header, index) => {
                    worksheet.getCell(row, index * 2 + 7).value = acumuladosP[index].toFixed(0) + "%";
                    worksheet.getCell(row, index * 2 + 8).value = acumuladosE[index].toFixed(0) + "%";
                    worksheet.getCell(row, index * 2 + 7).alignment = { horizontal: "center" };
                    worksheet.getCell(row, index * 2 + 8).alignment = { horizontal: "center" };
                    worksheet.getCell(row, index * 2 + 7).border = worksheet.getCell(row, index * 2 + 8).border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" }
                    };
                });
                row++;
                row++; // Espacio entre fases
            });
        }
        // **Generar archivo**
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, "Plan_Operativo_Anual.xlsx");
    };

    useImperativeHandle(ref, () => ({
        exportToExcel
    }));

    return null;
});

export default ExportPoa;
